import { resolveSchema, resolveDomainEvent } from './workflowSpec.js'
import { computeMidString, getLastCardSortkey } from './sortkey.js'
import {
  buildSchemaField,
  resolveFieldRefs,
  resolveFieldUpdateRefs,
  applyNestedFieldUpdates,
  buildSchemaRef
} from './schemaFields.js'
import { sanitizeCardDescription } from './sanitize.js'
import { DomainModelRole } from '../constants/cardTypes.js'
import { SchemaType } from '../constants/schema.js'

const generateId = () => globalThis.crypto.randomUUID()

const findCommandCardBySchemaId = (domainEvents, schemaId, cardTypesMap) => {
  for (let eventIndex = 0; eventIndex < domainEvents.length; eventIndex++) {
    const event = domainEvents[eventIndex]
    const cards = event.cards || []
    for (let cardIndex = 0; cardIndex < cards.length; cardIndex++) {
      const card = cards[cardIndex]
      const cardType = cardTypesMap[card.cardTypeId]
      if (cardType?.domainModelRole === DomainModelRole.COMMAND && card.schemaId === schemaId) {
        return { card, event, eventIndex, cardIndex }
      }
    }
  }

  return null
}

export const buildCreateCommandsMutation = ({ workflow, workflowId, commands, cardTypes }) => {
  if (!Array.isArray(commands) || commands.length === 0) {
    return { error: 'commands is required and must be a non-empty array' }
  }

  const cardTypesList = cardTypes || []
  const commandCardType = cardTypesList.find((cardType) => cardType.domainModelRole === DomainModelRole.COMMAND)
  if (!commandCardType) {
    return { error: 'Command card type not found in this workflow' }
  }
  const cardTypesMap = Object.fromEntries(cardTypesList.map((cardType) => [cardType.id, cardType]))

  const existingCommandNames = new Set(
    (workflow.schemas || [])
      .filter((schema) => schema.type === SchemaType.COMMAND)
      .map((schema) => schema.name)
  )
  const batchNames = new Set()
  const batchEventIds = new Set()

  const domainEvents = workflow.domainEvents || []
  let lastSortkey = getLastCardSortkey(domainEvents)

  const newSchemas = []
  const newCards = []

  for (const entry of commands) {
    const { domainEvent, name, fields } = entry || {}

    if (!domainEvent) {
      return { error: 'Each command must have a domainEvent ref' }
    }
    if (!name || typeof name !== 'string' || name.trim() === '') {
      return { error: 'Each command must have a non-empty name' }
    }

    const sanitizedName = sanitizeCardDescription(name)

    if (batchNames.has(sanitizedName)) {
      return { error: `Duplicate command name "${sanitizedName}" in the batch` }
    }
    batchNames.add(sanitizedName)

    if (existingCommandNames.has(sanitizedName)) {
      return { error: `Command "${sanitizedName}" already exists` }
    }

    const matchedEvent = resolveDomainEvent(domainEvent, workflow)
    if (!matchedEvent) {
      return { error: `Domain event not found for ref: ${domainEvent}` }
    }

    if (batchEventIds.has(matchedEvent.id)) {
      return { error: `Multiple commands target the same domain event "${matchedEvent.description}" in the batch` }
    }
    batchEventIds.add(matchedEvent.id)

    const existingCommandCard = (matchedEvent.cards || []).find((card) => {
      return cardTypesMap[card.cardTypeId]?.domainModelRole === DomainModelRole.COMMAND
    })
    if (existingCommandCard?.schemaId) {
      return { error: `Event "${matchedEvent.description}" already has a Command card. Use update_commands to change it.` }
    }

    const resolvedFields = resolveFieldRefs(fields, workflow)
    const newSchema = {
      id: generateId(),
      name: sanitizedName,
      type: SchemaType.COMMAND,
      fields: (resolvedFields || []).map((field) => buildSchemaField(field, SchemaType.COMMAND))
    }
    newSchemas.push({ ...newSchema, index: -1 })

    const eventIndex = domainEvents.findIndex((event) => event.id === matchedEvent.id)

    // A Command card with no schema is the state validation reports as "no parameters" —
    // link it rather than refusing, which would leave no tool able to fix it.
    if (existingCommandCard) {
      newCards.push({
        cardId: existingCommandCard.id,
        cardIndex: (matchedEvent.cards || []).indexOf(existingCommandCard),
        expectedVersion: existingCommandCard.version,
        description: existingCommandCard.description,
        sortkey: existingCommandCard.sortkey,
        cardTypeId: existingCommandCard.cardTypeId,
        done: existingCommandCard.done ?? false,
        schemaId: newSchema.id,
        boundedContext: existingCommandCard.boundedContext,
        cardinality: existingCommandCard.cardinality,
        releaseNo: existingCommandCard.releaseNo,
        eventId: matchedEvent.id,
        eventIndex
      })

      continue
    }

    const sortkey = computeMidString(lastSortkey, '')
    lastSortkey = sortkey

    newCards.push({
      cardIndex: -1,
      eventId: matchedEvent.id,
      eventIndex,
      description: name,
      done: false,
      cardTypeId: commandCardType.id,
      releaseNo: null,
      sortkey,
      boundedContext: null,
      schemaId: newSchema.id
    })
  }

  const schemaInput = {
    id: workflowId,
    expectedVersion: workflow.version,
    schemas: newSchemas,
    updateNote: {
      action: 'created commands',
      target: newSchemas.map((schema) => schema.name).join(', ')
    }
  }

  // cardInput's expectedVersion is threaded in by the caller after schemaInput persists.
  const cardInput = {
    workflowId,
    updateNote: {
      action: 'added command cards',
      target: newSchemas.map((schema) => schema.name).join(', ')
    },
    cards: newCards
  }

  return {
    schemaInput,
    cardInput,
    newSchemas,
    message: `${newSchemas.length} ${newSchemas.length === 1 ? 'command' : 'commands'} created successfully`,
    refs: newSchemas.map((schema) => ({ name: schema.name, ref: buildSchemaRef('commands', schema.name) }))
  }
}

export const buildUpdateCommandsMutation = ({ workflow, workflowId, commands, cardTypes }) => {
  if (!Array.isArray(commands) || commands.length === 0) {
    return { error: 'commands is required and must be a non-empty array' }
  }

  const cardTypesMap = Object.fromEntries((cardTypes || []).map((cardType) => [cardType.id, cardType]))

  const claimedSchemaIds = new Set()
  const claimedNewNames = new Set()
  const schemaInputs = []
  const changeSummaries = []
  const refsOut = []
  const pendingRenames = []

  for (const update of commands) {
    const { command: commandRef, name, addFields: rawAdds, updateFields: rawUpdates, removeFields } = update || {}

    if (!commandRef) {
      return { error: 'Each item must include a command ref' }
    }

    const resolved = resolveSchema(commandRef, workflow, cardTypesMap)
    if (!resolved) {
      return { error: `Command not found for ref: ${commandRef}` }
    }
    const schemaId = resolved.id

    if (claimedSchemaIds.has(schemaId)) {
      return { error: `Command "${commandRef}" appears more than once in the batch` }
    }
    claimedSchemaIds.add(schemaId)

    const schemaIndex = (workflow.schemas || []).findIndex((schema) => schema.id === schemaId)
    if (schemaIndex === -1) {
      return { error: `Command not found for ref: ${commandRef}` }
    }

    const addFields = resolveFieldRefs(rawAdds, workflow)
    const updateFields = resolveFieldUpdateRefs(rawUpdates, workflow)

    const schema = workflow.schemas[schemaIndex]
    const currentFields = [...(schema.fields || [])]
    const changes = []

    if (removeFields && removeFields.length > 0) {
      for (const fieldName of removeFields) {
        const fieldIndex = currentFields.findIndex((field) => field.name === fieldName)
        if (fieldIndex === -1) {
          return { error: `Field "${fieldName}" not found in command "${schema.name}"` }
        }
        currentFields.splice(fieldIndex, 1)
        changes.push(`removed "${fieldName}"`)
      }
    }

    if (updateFields && updateFields.length > 0) {
      for (const { fieldName, updates } of updateFields) {
        const fieldIndex = currentFields.findIndex((field) => field.name === fieldName)
        if (fieldIndex === -1) {
          return { error: `Field "${fieldName}" not found in command "${schema.name}"` }
        }

        if (updates.name && updates.name !== fieldName) {
          const collision = currentFields.find((field) => field.name === updates.name)
          if (collision) {
            return { error: `Field "${updates.name}" already exists in command "${schema.name}"` }
          }
        }

        const updatedField = { ...currentFields[fieldIndex] }

        if (updates.name !== undefined) {
          updatedField.name = updates.name
        }
        if (updates.hideInForm !== undefined) {
          updatedField.hideInForm = updates.hideInForm
        }
        if (updates.isRequired !== undefined) {
          updatedField.tags = updates.isRequired ? ['REQUIRED'] : []
        }
        if (updates.relatedEntityId !== undefined) {
          updatedField.relatedEntityId = updates.relatedEntityId
          if (!updatedField.relatedEntityId) {
            delete updatedField.fields
          }
        }
        if (updates.cardinality !== undefined) {
          updatedField.cardinality = updates.cardinality
        }
        if (updates.fields !== undefined) {
          const nested = applyNestedFieldUpdates(updatedField.fields, updates.fields)
          if (nested && nested.length > 0) {
            updatedField.fields = nested
          } else {
            delete updatedField.fields
          }
        }

        currentFields[fieldIndex] = updatedField
        changes.push(`updated "${fieldName}"`)
      }
    }

    if (addFields && addFields.length > 0) {
      for (const field of addFields) {
        if (currentFields.find((existing) => existing.name === field.name)) {
          return { error: `Field "${field.name}" already exists in command "${schema.name}"` }
        }
        currentFields.push(buildSchemaField(field, SchemaType.COMMAND))
        changes.push(`added "${field.name}"`)
      }
    }

    const updatedSchema = {
      ...schema,
      fields: currentFields
    }

    const sanitizedName = name !== undefined ? sanitizeCardDescription(name) : undefined
    if (sanitizedName !== undefined && sanitizedName !== schema.name) {
      const collision = workflow.schemas.find((other) => other.name === sanitizedName && other.type === SchemaType.COMMAND && other.id !== schemaId)
      if (collision) {
        return { error: `Command "${sanitizedName}" already exists` }
      }
      if (claimedNewNames.has(sanitizedName)) {
        return { error: `Command "${sanitizedName}" is set by more than one entry in the batch` }
      }
      claimedNewNames.add(sanitizedName)
      updatedSchema.name = sanitizedName
      changes.push(`renamed to "${sanitizedName}"`)
      pendingRenames.push({ schemaId, newName: sanitizedName })
    }

    if (changes.length === 0) {
      return { error: `No changes specified for "${schema.name}"` }
    }

    schemaInputs.push({
      ...updatedSchema,
      index: schemaIndex,
      expectedVersion: schema.version || null
    })
    changeSummaries.push(`"${schema.name}": ${changes.join(', ')}`)
    refsOut.push({
      name: updatedSchema.name,
      ref: buildSchemaRef('commands', updatedSchema.name)
    })
  }

  const schemaInput = {
    id: workflowId,
    expectedVersion: workflow.version,
    schemas: schemaInputs,
    updateNote: {
      action: 'updated commands',
      target: schemaInputs.map((schema) => schema.name).join(', ')
    }
  }

  // Renaming a command also renames its linked Command card.
  // cardInput's expectedVersion is threaded in by the caller after schemaInput persists.
  let cardInput = null

  if (pendingRenames.length > 0) {
    const cardsToUpdate = []

    for (const { schemaId, newName } of pendingRenames) {
      const match = findCommandCardBySchemaId(workflow.domainEvents || [], schemaId, cardTypesMap)
      if (!match) {
        continue
      }

      cardsToUpdate.push({
        cardId: match.card.id,
        cardIndex: match.cardIndex,
        expectedVersion: match.card.version,
        description: newName,
        sortkey: match.card.sortkey,
        cardTypeId: match.card.cardTypeId,
        done: match.card.done ?? false,
        schemaId: match.card.schemaId,
        boundedContext: match.card.boundedContext,
        releaseNo: match.card.releaseNo,
        eventId: match.event.id,
        eventIndex: match.eventIndex
      })
    }

    if (cardsToUpdate.length > 0) {
      cardInput = {
        workflowId,
        updateNote: {
          action: 'renamed command cards',
          target: pendingRenames.map((rename) => rename.newName).join(', ')
        },
        cards: cardsToUpdate
      }
    }
  }

  const updatedCount = schemaInput.schemas.length

  return {
    schemaInput,
    cardInput,
    changeSummaries,
    message: `${updatedCount} ${updatedCount === 1 ? 'command' : 'commands'} updated: ${changeSummaries.join(' | ')}`,
    refs: refsOut
  }
}

export const buildDeleteCommandMutation = ({ workflow, workflowId, command, cardTypes }) => {
  if (!command) {
    return { error: 'Command ref is required' }
  }

  const cardTypesMap = Object.fromEntries((cardTypes || []).map((cardType) => [cardType.id, cardType]))

  const resolved = resolveSchema(command, workflow, cardTypesMap)
  if (!resolved) {
    return { error: `Command not found for ref: ${command}` }
  }
  const schemaId = resolved.id

  const schemaIndex = (workflow.schemas || []).findIndex((schema) => schema.id === schemaId)
  if (schemaIndex === -1) {
    return { error: 'Command not found' }
  }

  const schema = workflow.schemas[schemaIndex]
  const match = findCommandCardBySchemaId(workflow.domainEvents || [], schemaId, cardTypesMap)

  // The linked Command card (if any) is deleted first; both steps' expectedVersion is threaded by the caller.
  const cardDelete = match
    ? {
        workflowId,
        eventId: match.event.id,
        eventIndex: match.eventIndex,
        cardId: match.card.id,
        cardIndex: match.cardIndex,
        cardDescription: match.card.description
      }
    : null

  const schemaInput = {
    id: workflowId,
    deletedSchemas: [{ deleteIndex: schemaIndex, deleteId: schemaId }],
    updateNote: {
      action: 'deleted command',
      target: schema.name
    }
  }

  return { cardDelete, schemaInput, schema, message: `Command "${schema.name}" deleted successfully` }
}
